import { ExternalLink as ExternalIcon, ShieldQuestion } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { isExternal } from '../lib/site';

type Ask = (url: string) => void;

const LeavingContext = createContext<Ask | null>(null);

/**
 * "You are about to leave GoSpeedNet Dispatch."
 *
 * Worth a confirmation for a reason beyond politeness: a dispatcher may be
 * halfway through a ticket, and a stray click that navigates away loses what
 * they had typed. The dialog also shows the address in full, which is the
 * habit that makes a bad link in a customer note survivable — somebody reads
 * where it actually goes before they arrive there.
 *
 * Deliberately not applied to the technician's own job links or the tracking
 * page: those are this application, and asking somebody to confirm going to
 * their own next screen trains them to click through dialogs without reading.
 */
export function LeavingProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<string | null>(null);

  const ask = useCallback<Ask>((url) => setPending(url), []);

  // Escape closes it. A confirmation you cannot dismiss with the keyboard is a
  // confirmation people learn to fear rather than read.
  useEffect(() => {
    if (!pending) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPending(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending]);

  return (
    <LeavingContext.Provider value={ask}>
      {children}

      {pending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="leaving-title"
          onClick={(event) => {
            if (event.target === event.currentTarget) setPending(null);
          }}
        >
          <div className="w-full max-w-md rounded-2xl bg-card p-6 shadow-card-hover">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50">
              <ShieldQuestion className="h-5 w-5 text-primary-600" aria-hidden="true" />
            </div>

            <h2 id="leaving-title" className="font-heading text-lg">
              Leaving GoSpeedNet Dispatch
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              This opens another site in a new tab. Anything you were typing here stays where it is.
            </p>

            {/* The full address, wrapped rather than truncated. A shortened URL
                hides the part that matters on a link somebody else wrote. */}
            <p className="mt-3 break-all rounded-xl bg-muted px-3 py-2 font-mono text-xs">
              {pending}
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <a
                href={pending}
                target="_blank"
                rel="noreferrer noopener"
                className="btn-primary"
                onClick={() => setPending(null)}
              >
                <ExternalIcon className="h-4 w-4" aria-hidden="true" />
                Open it
              </a>
              <button type="button" className="btn-secondary" onClick={() => setPending(null)}>
                Stay here
              </button>
            </div>
          </div>
        </div>
      )}
    </LeavingContext.Provider>
  );
}

/**
 * A link that confirms before it leaves.
 *
 * Falls back to a plain anchor when the destination is on this origin, so
 * internal navigation is never interrupted.
 */
export function OutboundLink({
  href,
  className = '',
  children,
  title,
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
  title?: string;
}) {
  const ask = useContext(LeavingContext);
  const external = isExternal(href);

  if (!external || !ask) {
    return (
      <a href={href} className={className} title={title}>
        {children}
      </a>
    );
  }

  return (
    <a
      href={href}
      className={className}
      title={title}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(event) => {
        // Only intercept a plain click. Ctrl/⌘-click, middle-click and "open in
        // new tab" already mean "I know where I am going", and hijacking those
        // is the kind of helpfulness people disable extensions to escape.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        ask(href);
      }}
    >
      {children}
    </a>
  );
}
